import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { setListing } from '../../../../lib/listingsSlice';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faStar, faGem, faCrown } from '@fortawesome/free-solid-svg-icons';
import { format } from 'date-fns';
import toast, { Toaster } from 'react-hot-toast';
import Page from '../../../../components/Page';
import Breadcrumb from '../../../../components/Breadcrumb';
import PrimaryButton from '../../../../components/PrimaryButton';
import client from '../../../../lib/feathers';

const TIER_LABEL = {
  claimed: 'Claimed', enhanced: 'Enhanced', premium: 'Premium', reserve: 'Reserve',
};

// Presentation and pricing only. The FEATURE BULLETS are no longer authored
// here — they come from `industry/feature-catalog`, which is the same list the
// tier bitmasks are derived from. Nothing connected the string "Photo gallery"
// to the bit that gates it before, which is how the page came to promise things
// the platform did not grant and gate things it never sold.
//
// `fallbackFeatures` is what renders if that request fails, so a network blip
// shows slightly stale copy rather than four empty cards.
const TIERS = [
  {
    id: 'claimed',
    name: 'Claimed',
    icon: faCheck,
    color: 'border-gray-300 bg-gray-50',
    badgeColor: 'bg-gray-100 text-gray-800',
    description: 'Basic listing presence',
    fallbackFeatures: ['Listing on VinoSeeker', 'Business hours', 'Contact information',
      'Accept check-ins', 'Wine list management', 'Flight management', 'Basic analytics',
      'Passport participation'],
    price: null,
  },
  {
    id: 'enhanced',
    name: 'Enhanced',
    icon: faStar,
    color: 'border-blue-300 bg-blue-50',
    badgeColor: 'bg-blue-100 text-blue-800',
    description: 'Free with VinoSeeker signage',
    fallbackFeatures: ['Everything in Claimed', 'Description of your winery',
      'Custom welcome message', 'Your own QR code'],
    price: null,
    priceNote: 'Free with signage',
  },
  {
    id: 'premium',
    name: 'Premium',
    icon: faGem,
    color: 'border-purple-300 bg-purple-50',
    badgeColor: 'bg-purple-100 text-purple-800',
    description: 'Full-featured business tools',
    fallbackFeatures: [
      'Everything in Enhanced',
      'Your logo',
      'Wine club promotion',
      'Booking link',
      'Photo gallery',
      'User photo moderation',
      'Advanced analytics & benchmarks',
      'Advanced passport analytics',
      'Priority support',
    ],
    prices: {
      1: { amount: 300, label: '$300/mo' },
      3: { amount: 275, label: '$275/mo', save: 75, note: 'Save $75' },
      6: { amount: 250, label: '$250/mo', save: 300, note: 'Save $300', best: true },
    },
  },
  {
    id: 'reserve',
    name: 'Reserve',
    icon: faCrown,
    color: 'border-amber-300 bg-amber-50',
    badgeColor: 'bg-amber-100 text-amber-800',
    description: 'Premium, plus placement and itineraries',
    fallbackFeatures: [
      'Everything in Premium',
      'Featured placement',
      'Custom itineraries',
      'Dedicated account manager',
    ],
    prices: {
      1: { amount: 500, label: '$500/mo' },
      3: { amount: 458, label: '$458/mo', save: 125, note: 'Save $125' },
      6: { amount: 417, label: '$417/mo', save: 500, note: 'Save $500', best: true },
    },
  },
];

const INTERVALS = [
  { value: 1, label: 'Monthly' },
  { value: 3, label: '3-Month' },
  { value: 6, label: '6-Month' },
];

const STATUS_CONFIG = {
  active: 'bg-green-100 text-green-800',
  past_due: 'bg-yellow-100 text-yellow-800',
  canceled: 'bg-red-100 text-red-800',
  trialing: 'bg-blue-100 text-blue-800',
  incomplete: 'bg-gray-100 text-gray-600',
};

export default function IndustrySubscription() {
  const { lid } = useParams();
  const dispatch = useDispatch();
  const listing = useSelector((s) => s.listings.selected);

  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState({});
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedInterval, setSelectedInterval] = useState(1);
  // tier id -> the bullets that tier should show, from the feature catalog.
  const [catalogFeatures, setCatalogFeatures] = useState(null);
  const [showEnhancedModal, setShowEnhancedModal] = useState(false);

  // The bullets come from the same list the tier bitmasks are derived from, so
  // the page cannot promise something the platform does not actually grant.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await client.service('industry/feature-catalog').find({});
        if (cancelled) return;
        const byTier = {};
        const order = (res.tiers || []).map((t) => t.tier);
        (res.tiers || []).forEach((t, i) => {
          // Each card leads with a rollup of the one below rather than
          // repeating it, which is how the page has always read.
          const rollup = i > 0
            ? [{ label: `Everything in ${TIER_LABEL[order[i - 1]] || order[i - 1]}` }]
            : [];
          byTier[t.tier] = [...rollup, ...(t.features || [])];
        });
        setCatalogFeatures(byTier);
      } catch (e) {
        // Leave it null and fall back to the copy bundled with the page.
        console.log('feature catalog unavailable', e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (lid && (!listing || listing.id !== lid)) {
      const loadListing = async () => {
        try {
          const service = client.service('industry/listing');
          const result = await service.get(lid);
          dispatch(setListing(result));
        } catch (err) {
          console.log('Error loading listing:', err);
        }
      };
      loadListing();
    }
  }, [lid, listing, dispatch]);

  const loadSubscription = useCallback(async () => {
    try {
      const service = client.service('industry/listing-subscription');
      const result = await service.find({ query: { listingId: lid } });
      setSubscription(result);
      if (result.plans) setPlans(result.plans);
      if (result.events) setEvents(result.events);
    } catch (err) {
      console.log('Error loading subscription:', err);
    } finally {
      setLoading(false);
    }
  }, [lid]);

  useEffect(() => {
    if (lid) loadSubscription();
  }, [lid, loadSubscription]);

  const currentTier = subscription?.subscriptionTier || 'claimed';
  const currentStatus = subscription?.subscriptionStatus;

  const handleCheckout = async (tierId) => {
    setActionLoading(true);
    try {
      const service = client.service('industry/listing-subscription');
      // plans is { premium: { prices: { monthly: {id, amount}, quarterly: {id}, semi_annual: {id} } }, reserve: {...} }
      const tierPlans = plans[tierId];
      const intervalMap = { 1: 'monthly', 3: 'quarterly', 6: 'semi_annual' };
      const period = intervalMap[selectedInterval];
      const price = tierPlans?.prices?.[period];
      if (!price?.id) {
        toast.error('No price found for this plan and interval');
        setActionLoading(false);
        return;
      }
      const result = await service.create({
        action: 'checkout',
        listingId: lid,
        priceId: price.id,
      });
      if (result.url) {
        window.location.href = result.url;
      }
    } catch (err) {
      toast.error(err.message || 'Failed to start checkout');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePortal = async () => {
    setActionLoading(true);
    try {
      const service = client.service('industry/listing-subscription');
      const result = await service.create({
        action: 'portal',
        listingId: lid,
      });
      if (result.url) {
        window.location.href = result.url;
      }
    } catch (err) {
      toast.error(err.message || 'Failed to open billing portal');
    } finally {
      setActionLoading(false);
    }
  };

  const getPriceDisplay = (tier, interval) => {
    if (!tier.prices) return null;
    return tier.prices[interval];
  };

  if (loading) {
    return (
      <Page>
        <div className="text-gray-500">Loading subscription...</div>
      </Page>
    );
  }

  return (
    <Page>
      <Toaster toastOptions={{ duration: 3000 }} />
      <div className="space-y-6">
        <div className="pb-5 border-b border-gray-200">
          <Breadcrumb
            elements={[
              { name: listing?.name || 'Listing', href: `/industry/listings/${lid}` },
              { name: 'Subscription' },
            ]}
          />
        </div>

        {/* Current Subscription Status */}
        <div className="bg-white shadow sm:rounded-lg overflow-hidden">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg font-medium text-gray-900">Current Subscription</h3>
          </div>
          <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
            <div className="flex items-center gap-3 mb-4">
              <span
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${
                  (TIERS.find((t) => t.id === currentTier) || TIERS[0]).badgeColor
                }`}
              >
                <FontAwesomeIcon
                  icon={(TIERS.find((t) => t.id === currentTier) || TIERS[0]).icon}
                  className="w-3.5 h-3.5"
                />
                {(TIERS.find((t) => t.id === currentTier) || TIERS[0]).name}
              </span>
              {currentStatus && (
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    STATUS_CONFIG[currentStatus] || 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {currentStatus}
                </span>
              )}
            </div>

            {subscription?.currentPeriodEnd && (
              <dl className="space-y-2">
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-500">Renewal Date</dt>
                  <dd className="text-sm text-gray-900">
                    {format(new Date(subscription.currentPeriodEnd), 'MMMM d, yyyy')}
                  </dd>
                </div>
                {subscription?.cancelAtPeriodEnd && (
                  <div className="flex justify-between">
                    <dt className="text-sm text-gray-500">Status</dt>
                    <dd className="text-sm text-red-600 font-medium">Cancels at period end</dd>
                  </div>
                )}
              </dl>
            )}

            {(subscription?.subscriptionStatus === 'active' ||
              subscription?.subscriptionStatus === 'past_due') && (
              <div className="mt-4">
                <button
                  type="button"
                  onClick={handlePortal}
                  disabled={actionLoading}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Manage Billing
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Billing Interval Toggle */}
        <div className="flex justify-center">
          <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1">
            {INTERVALS.map((interval) => (
              <button
                key={interval.value}
                type="button"
                onClick={() => setSelectedInterval(interval.value)}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  selectedInterval === interval.value
                    ? 'bg-primary text-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {interval.label}
              </button>
            ))}
          </div>
        </div>

        {/* Plan Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {TIERS.map((tier) => {
            const isCurrent = tier.id === currentTier;
            const priceInfo = getPriceDisplay(tier, selectedInterval);
            const isPaid = !!tier.prices;

            return (
              <div
                key={tier.id}
                className={`relative rounded-lg border-2 p-5 ${
                  isCurrent ? 'ring-2 ring-primary ' + tier.color : tier.color
                }`}
              >
                {priceInfo?.best && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-400 text-amber-900">
                      <FontAwesomeIcon icon={faStar} className="w-3 h-3" />
                      Best Value
                    </span>
                  </div>
                )}

                <div className="text-center mb-4">
                  <FontAwesomeIcon icon={tier.icon} className="w-8 h-8 mb-2 text-gray-600" />
                  <h4 className="text-lg font-semibold text-gray-900">{tier.name}</h4>
                  <p className="text-sm text-gray-500 mt-1">{tier.description}</p>
                </div>

                <div className="text-center mb-4">
                  {isPaid && priceInfo ? (
                    <>
                      <span className="text-2xl font-bold text-gray-900">{priceInfo.label}</span>
                      {priceInfo.note && (
                        <p className="text-sm text-green-600 font-medium mt-1">{priceInfo.note}</p>
                      )}
                    </>
                  ) : (
                    <span className="text-2xl font-bold text-gray-900">
                      {tier.priceNote || 'Free'}
                    </span>
                  )}
                </div>

                <ul className="space-y-2 mb-4">
                  {(catalogFeatures?.[tier.id] || tier.fallbackFeatures).map((feature) => {
                    // Catalog entries are {label, detail}; the offline fallback
                    // is still plain strings.
                    const label = typeof feature === 'string' ? feature : feature.label;
                    const detail = typeof feature === 'string' ? null : feature.detail;
                    return (
                      <li key={label} className="flex items-start gap-2 text-sm text-gray-600">
                        <FontAwesomeIcon icon={faCheck} className="w-3.5 h-3.5 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>
                          {label}
                          {detail && (
                            <span className="block mt-0.5 text-xs text-gray-500">{detail}</span>
                          )}
                        </span>
                      </li>
                    );
                  })}
                </ul>

                {isCurrent ? (
                  <div className="text-center">
                    <span className="inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium bg-gray-200 text-gray-700">
                      Current Plan
                    </span>
                  </div>
                ) : isPaid ? (
                  <div className="text-center">
                    <PrimaryButton onClick={() => handleCheckout(tier.id)} disabled={actionLoading}>
                      {currentTier === 'claimed' || currentTier === 'enhanced'
                        ? 'Upgrade'
                        : 'Change Plan'}
                    </PrimaryButton>
                  </div>
                ) : tier.id === 'enhanced' && currentTier === 'claimed' ? (
                  <div className="text-center">
                    <PrimaryButton onClick={() => setShowEnhancedModal(true)} disabled={actionLoading}>
                      Learn More
                    </PrimaryButton>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>

        {/* Event History */}
        {events.length > 0 && (
          <div className="bg-white shadow sm:rounded-lg overflow-hidden">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Transaction History</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Event
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Details
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {events.map((evt, i) => (
                    <tr key={evt.id || i}>
                      <td className="px-4 py-3 text-sm text-gray-400 whitespace-nowrap">
                        {format(new Date(evt.createdAt), 'MMM d, yyyy h:mm a')}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">{evt.eventType || evt.type}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{evt.summary || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Enhanced Tier Modal */}
      {showEnhancedModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowEnhancedModal(false)} />
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6 z-10">
              <div className="text-center mb-4">
                <FontAwesomeIcon icon={faStar} className="w-10 h-10 text-blue-500 mb-3" />
                <h3 className="text-lg font-semibold text-gray-900">Upgrade to Enhanced</h3>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                The Enhanced tier is free for wineries that help promote VinoSeeker in their tasting room. This includes displaying signage, QR codes, or other promotional materials that help visitors discover the app.
              </p>
              <p className="text-sm text-gray-600 mb-6">
                To get started, reach out to our team and we&apos;ll work with you on the best way to feature VinoSeeker at your location.
              </p>
              <div className="bg-blue-50 rounded-lg p-4 mb-6">
                <p className="text-sm font-medium text-blue-800">Contact us:</p>
                <a href="mailto:service@vinoseeker.com" className="text-sm text-blue-600 hover:text-blue-800 font-medium">
                  service@vinoseeker.com
                </a>
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowEnhancedModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Close
                </button>
                <a
                  href={`mailto:service@vinoseeker.com?subject=${encodeURIComponent(`Enhanced Tier Interest — ${listing?.name || ''}`)}`}
                  className="flex-1 px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 text-center"
                >
                  Send Email
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </Page>
  );
}
